/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react"
import { ChevronLeft, ChevronRight, Help } from "@mui/icons-material";
import { Button, IconButton, Tooltip } from "@mui/material";
import { ProductCategoriesTree, useInventoryState } from "../../../hooks/useInventoryState";
import { useThemeState } from "../../../hooks/useThemeState";
import { CatalogMenu } from "../../CatalogMenu";
import { CategoryTreeControl } from "./CategoryTree";
import { CustomScrollbars } from "../../CustomScrollbars/CustomScrollbars";
import { SelectLocation } from "../../CheckoutForm/SelectLocation";
import { useStorefrontSettingsState } from "../../../hooks/useStorefrontSettingsState";
import { CheckoutDate } from "../../CheckoutForm/CheckoutDate";
import { useTranslate } from "../../../hooks/useTranslate/useTranslate";
import { useTailwindMediaQueries } from "../../../hooks/useTailwindMediaQueries";
import { SquareXIcon } from "lucide-react";

//-------------------------------------------------------------------------------------------------------
export type SidebarProps = {
    
}

export const Sidebar: React.FC<SidebarProps> = props => {
    const translate = useTranslate();
    const themeState = useThemeState()
    const theme = themeState.get().theme;
    const inventoryState = useInventoryState();
    const sidebarExpanded = inventoryState.get().sidebarExpanded;
    const sidebarCollapsedWidth = 40;
    const settingsState = useStorefrontSettingsState();
    const isUniversity = settingsState.get().settings?.IsUniversity ?? false;
    const mediaQueries = useTailwindMediaQueries();
    return (
        <div css={css`flex:1 1 0;display:flex;font-size:${mediaQueries.desktop ? '1rem' : '1rem'};`}>
            <div
                css={css`
                    flex: 1 1 0;
                    display:  ${sidebarExpanded ? 'flex' : 'none'};
                    flex-direction:column;
                    overflow:hidden;
                `}
                style={{
                    marginLeft: sidebarExpanded ? '0' : `${sidebarCollapsedWidth}px`
                }}
            >
                {!isUniversity && (<>
                    <div
                        css={css`
                            flex:0;
                            padding:.5em .5em 0 .5em;
                            display:flex;
                        `}>
                        <SelectLocation disabled={false} />
                        {/*<SelectWarehouse disabled={false} />*/}
                        <div css={css`width:24px;`}></div>
                    </div>
                </>)}

                {(inventoryState.get().catalog?.HasRental ?? false) && (mediaQueries.phone || mediaQueries.desktop) && (
                    <div
                        css={css`
                            flex:0 0 auto;
                            padding:0 .5em;
                            .field {
                                margin:1em 0 2px 0;
                            }
                            .caption {
                                text-align:center;
                                color: ${theme.palette.siteSideBarText};
                            }
                            .flex {
                                display:flex;
                            }
                            `}>
                        <div className="flex">
                            <div css={css`display:flex;flex-wrap:wrap;`}>
                                <div css={css`padding-top:12px;`}>
                                    <CheckoutDate type="startdate" fullwidth={true} catalogId={inventoryState.get().catalog?.Id} />
                                </div>
                                <div css={css`padding-top:12px;`}>
                                    <CheckoutDate type="stopdate" fullwidth={true} catalogId={inventoryState.get().catalog?.Id} />
                                </div>
                            </div>
                            <div css={css`display:flex;justify-content:flex-end;align-items:center;`}>
                                <Tooltip title={translate('Reservations for different date ranges must be submitted as separate Orders')} arrow>
                                    <Help />
                                </Tooltip>
                            </div>
                        </div>
                    </div>
                )}
                <div
                    css={css`
                        flex:0;
                        font-size:1rem;
                        line-height:2rem;
                        font-weight:700;
                        color: ${theme.palette.siteSideBarText};
                        padding:.5em;
                        display:flex;
                        align-items: center;
                        justify-content: flex-start;
                        /*font-family: 'Ropa Sans';*/
                        font-size:1.1em;`}>
                    {/* <ImportContacts css={css`padding:0 4px;`} /> */}
                    <CatalogMenu font-size="small" />
                </div>
                <div
                    css={css`
                        flex:1 1 0;
                        padding: 0 1em;
                        display:flex;
                        flex-direction:column;
                    `}
                >
                    <CustomScrollbars
                        overridecss={`
                            flex: 1 1 0;
                            overflow-x:hidden;
                        `}>
                        {inventoryState.get().catalog &&
                            <CategoryTreeControl
                                catalog={inventoryState.get().catalog}
                                rentalCategoriesTree={inventoryState.get().productCategoriesTree as ProductCategoriesTree} />
                        }
                    </CustomScrollbars>

                </div>
            </div>

            {/* Expand/Collapse button */}
            {mediaQueries.minw_md && (
                <div css={css`flex:0 0 auto;display:flex;flex:0 0 auto;justify-content:flex-end;`}>
                    <div css={css`flex:0 0 auto;`}
                        onClick={async e => {
                            return Promise.resolve().then(() => {
                                inventoryState.setSidebarExpanded(!sidebarExpanded)
                            })
                        }}>
                        <IconButton
                            aria-label="Toggle">
                            {sidebarExpanded && (
                                <ChevronLeft />
                            )}
                            {!sidebarExpanded && (
                                <ChevronRight />
                            )}
                        </IconButton>
                    </div>
                </div>
            )}
            {(!mediaQueries.minw_md && sidebarExpanded) && (
                <div css={css`flex:0 0 auto;display:flex;flex:0 0 auto;justify-content:flex-end;`}>
                    <div css={css`
                        flex:0 0 auto;
                        .MuiButtonBase-root {
                            margin: 1rem;
                            /*background-color: #D32F2F;*/
                            color: #ffffff;
                            min-width: 32px;
                        }
                        `}
                        onClick={async e => {
                            return Promise.resolve().then(() => {
                                inventoryState.setSidebarExpanded(!sidebarExpanded);
                            })
                        }}
                    >
                        <Button
                            size="small"
                            variant="text"
                        >
                            <SquareXIcon css={css`color:oklch(0.65 0.3 29.34);width:2rem;height:2rem;box-shadow: 0 0px 32px rgba(255, 0, 0, 0.3);`} />
                            {/* <Close /> */}
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}